import { DialogProps } from "@mui/material";
import React, { FC, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../main/hooks/hooks";
import { callUpdatePassword, PasswordPayload, selectLoggedInUser } from "../../../main/slices/authSlice";
import { setToastMessage } from "../../../main/slices/utilSlice";
import CheckmateDialogBase from "../CheckmateDialogBase/CheckmateDialogBase";
import CheckmateButton from "../FormControls/Button/Button";
import CheckmateTextInput from "../FormControls/CheckmateTextInput/CheckmateTextInput";
import styles from '../../../assets/styles/variables.module.scss';
interface IError {
    [fieldName: string]: string
}

const initialFormValue = {
    currentPassword: '',
    password: '',
    confirmPassword: ''
}

const PASSWORD_HELPER_TEXT = 'Password must be 8-64 characters and contain at least 3 of: uppercase, lowercase, numbers, symbols';

const PasswordChangeModal: FC<DialogProps> = ({onClose, ...props}) => {
    const dispatch = useAppDispatch();
    const [passwordForm, setPasswordForm] = useState<PasswordPayload>({...initialFormValue});
    
    const [errors, setErrors] = useState<IError>({});

    const user = useAppSelector(selectLoggedInUser);

    const handleFormChange = (value: any, fieldName: string) => {
        setPasswordForm({ ...passwordForm, [fieldName]: value });
        const tempErrors = {...errors};
        delete tempErrors[fieldName]
        setErrors(tempErrors);
    }

    const handleUpdatePassword = async () => {
        const tempErrors = validateForm()
        if (Object.keys(tempErrors)?.length) {
            setErrors(tempErrors);
            return;
        }
        const response = await dispatch(callUpdatePassword({
            password: passwordForm,
            user
        }));

        if (response.payload) {
            dispatch(setToastMessage({
                type: 'success',
                message: 'Password was successfully updated.'
            }))
        } else {
            dispatch(setToastMessage({
                type: 'error',
                message: 'There was an issue updating your password.'
            }))
        }
        setPasswordForm({...initialFormValue});
        onClose({}, 'escapeKeyDown') as any;
    }

    const validateForm = () => {
        const tempErrors: IError = {}

        if (!passwordForm.currentPassword) {
            tempErrors.currentPassword = 'Required';
        }

        if (!passwordForm.password) {
            tempErrors.password = 'Required';
        } else if (!isValidPassword(passwordForm.password)) {
            tempErrors.password = 'Password must be 8-64 characters and contain at least 3 of: uppercase, lowercase, numbers, symbols';
        }

        if (!passwordForm.confirmPassword) {
            tempErrors.confirmPassword = 'Required';
        } else if (passwordForm.confirmPassword !== passwordForm.password) {
            tempErrors.confirmPassword = 'Passwords do not match';
        }
        return tempErrors;
    }

    const isValidPassword = (password: string): boolean => {
        if (!password || password.length < 8 || password.length > 64) return false;

        let characterClasses = 0;
        if (/[A-Z]/.test(password)) characterClasses++; // Uppercase
        if (/[a-z]/.test(password)) characterClasses++; // Lowercase
        if (/[0-9]/.test(password)) characterClasses++; // Numbers
        if (/[^A-Za-z0-9]/.test(password)) characterClasses++; // Symbols

        return characterClasses >= 3;
    }

    const handleClose = () => {
        setPasswordForm({
            ...initialFormValue
        })
        setErrors({});
        onClose({}, 'escapeKeyDown');
    }

    return <CheckmateDialogBase
        title={'Update Password'}
        fullWidth={true}
        modalContent={<div className="form">
            <div className='form-row'>
                <CheckmateTextInput
                    label={'Current Password'}
                    fieldName={'currentPassword'}
                    value={passwordForm.currentPassword}
                    onChange={handleFormChange}
                    type={'password'}
                    error={errors?.currentPassword}
                    color={process.env.REACT_APP_SKIN_NAME === 'atai' ? styles.color_on_background : 'default'}
                    focusColor={process.env.REACT_APP_SKIN_NAME === 'atai' ? styles.color_focus_border : 'default'}
                    borderColor={process.env.REACT_APP_SKIN_NAME === 'atai' ? styles.color_tertiary : 'default'}
                />
            </div>
            <div className='form-row'>
                <CheckmateTextInput
                    label={'New Password'}
                    fieldName={'password'}
                    value={passwordForm.password}
                    onChange={handleFormChange}
                    type={'password'}
                    error={errors?.password}
                    helperText={PASSWORD_HELPER_TEXT}
                    FormHelperTextProps={{
                        sx: {
                            color: process.env.REACT_APP_SKIN_NAME === 'atai' ? styles.color_on_background : 'default',
                        },
                    }}
                    color={process.env.REACT_APP_SKIN_NAME === 'atai' ? styles.color_on_background : 'default'}
                    focusColor={process.env.REACT_APP_SKIN_NAME === 'atai' ? styles.color_focus_border : 'default'}
                    borderColor={process.env.REACT_APP_SKIN_NAME === 'atai' ? styles.color_tertiary : 'default'}
                />
            </div>
            <div className='form-row'>
                <CheckmateTextInput
                    label={'Confirm New Password'}
                    fieldName={'confirmPassword'}
                    value={passwordForm.confirmPassword}
                    onChange={handleFormChange}
                    type={'password'}
                    error={errors?.confirmPassword}
                    color={process.env.REACT_APP_SKIN_NAME === 'atai' ? styles.color_on_background : 'default'}
                    focusColor={process.env.REACT_APP_SKIN_NAME === 'atai' ? styles.color_focus_border : 'default'}
                    borderColor={process.env.REACT_APP_SKIN_NAME === 'atai' ? styles.color_tertiary : 'default'}
                />
            </div>
        </div>}
        actionButtons={ <>
            <CheckmateButton
                variant="outlined"
                color="primary"
                label="Cancel"
                onClick={handleClose as any}
            />
            <CheckmateButton
                variant="contained"
                color="primary"
                label="Update Password"
                onClick={handleUpdatePassword}
            />
        </>}
        onClose={handleClose as any}
        {...props}
    />

}

export default PasswordChangeModal